/** Absent, null, empty string, or empty list. A field answered with `false` or
 *  `0` is answered, which is why this is not a truthiness check. */
export function isBlank(value) {
    if (value === undefined || value === null)
        return true;
    if (typeof value === "string")
        return value.trim() === "";
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
/** Read a field key out of the answer document, honouring the current row. */
export function resolveValue(fieldKey, scope) {
    const parts = fieldKey.split(".");
    if (scope.scopeEntityKey && scope.scopeRow && fieldKey.startsWith(`${scope.scopeEntityKey}.`)) {
        const leaf = parts.slice(scope.scopeEntityKey.split(".").length).join(".");
        return walk(scope.scopeRow, leaf.split("."));
    }
    return walk(scope.root, parts);
}
function walk(source, parts) {
    let current = source;
    for (const part of parts) {
        if (current === null || current === undefined)
            return undefined;
        if (Array.isArray(current)) {
            // A bare key cannot address one row of a repeating entity. Returning the
            // list lets `contains` and `is_present` still say something useful.
            return undefined;
        }
        if (typeof current !== "object")
            return undefined;
        current = current[part];
    }
    return current;
}
/** Both sides of a comparison as numbers, or null when either one is not. */
function asNumbers(left, right) {
    const a = toNumber(left);
    const b = toNumber(right);
    if (a === null || b === null)
        return null;
    return [a, b];
}
function toNumber(value) {
    if (typeof value === "number")
        return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value.replace(/[,\s]/g, ""));
        return Number.isFinite(parsed) ? parsed : null;
    }
    if (value instanceof Date)
        return value.getTime();
    return null;
}
/** Loose equality that treats the string "5" and the number 5 as the same
 *  answer, because a select posts strings and a number field posts numbers. */
function looselyEqual(left, right) {
    if (left === right)
        return true;
    if (isBlank(left) && isBlank(right))
        return true;
    const numbers = asNumbers(left, right);
    if (numbers)
        return numbers[0] === numbers[1];
    if (typeof left === "boolean" || typeof right === "boolean") {
        return toBoolean(left) === toBoolean(right);
    }
    if (left === null || left === undefined || right === null || right === undefined)
        return false;
    return String(left) === String(right);
}
function toBoolean(value) {
    if (typeof value === "boolean")
        return value;
    if (typeof value === "string") {
        const normalised = value.trim().toLowerCase();
        if (["true", "yes", "y", "1"].includes(normalised))
            return true;
        if (["false", "no", "n", "0"].includes(normalised))
            return false;
    }
    if (typeof value === "number")
        return value !== 0;
    return null;
}
function compare(op, left, right) {
    switch (op) {
        case "is_blank":
            return isBlank(left);
        case "is_present":
            return !isBlank(left);
        case "eq":
            return looselyEqual(left, right);
        case "ne":
            return !looselyEqual(left, right);
        case "gt":
        case "gte":
        case "lt":
        case "lte": {
            const numbers = asNumbers(left, right);
            if (numbers) {
                const [a, b] = numbers;
                if (op === "gt")
                    return a > b;
                if (op === "gte")
                    return a >= b;
                if (op === "lt")
                    return a < b;
                return a <= b;
            }
            // Dates arrive as ISO strings, which order correctly as strings.
            if (typeof left === "string" && typeof right === "string") {
                if (op === "gt")
                    return left > right;
                if (op === "gte")
                    return left >= right;
                if (op === "lt")
                    return left < right;
                return left <= right;
            }
            return false;
        }
        case "in":
        case "not_in": {
            const list = Array.isArray(right) ? right : [right];
            const found = Array.isArray(left)
                ? left.some((item) => list.some((candidate) => looselyEqual(item, candidate)))
                : list.some((candidate) => looselyEqual(left, candidate));
            return op === "in" ? found : !found;
        }
        case "contains": {
            if (Array.isArray(left))
                return left.some((item) => looselyEqual(item, right));
            if (isBlank(left))
                return false;
            return String(left).toLowerCase().includes(String(right ?? "").toLowerCase());
        }
        default:
            return false;
    }
}
export function isComparison(condition) {
    return typeof condition.field === "string";
}
/**
 * Evaluate a condition tree. An absent or malformed condition is true, so a
 * field with no `visibleWhen` is simply always shown.
 */
export function evaluate(condition, scope) {
    if (!condition)
        return true;
    if ("all" in condition && Array.isArray(condition.all)) {
        return condition.all.every((child) => evaluate(child, scope));
    }
    if ("any" in condition && Array.isArray(condition.any)) {
        return condition.any.length === 0 || condition.any.some((child) => evaluate(child, scope));
    }
    if ("not" in condition && condition.not) {
        return !evaluate(condition.not, scope);
    }
    if (isComparison(condition)) {
        return compare(condition.op, resolveValue(condition.field, scope), condition.value);
    }
    return true;
}
/** Every field key a condition tree reads. Used to refuse a condition that
 *  depends on a field the product has not attached. */
export function referencedFields(condition) {
    const found = new Set();
    const visit = (node) => {
        if (!node)
            return;
        if ("all" in node && Array.isArray(node.all))
            return node.all.forEach(visit);
        if ("any" in node && Array.isArray(node.any))
            return node.any.forEach(visit);
        if ("not" in node && node.not)
            return visit(node.not);
        if (isComparison(node))
            found.add(node.field);
    };
    visit(condition);
    return [...found];
}
//# sourceMappingURL=conditions.js.map